import { Button } from "@egaranti/components";
import { useToast } from "@egaranti/components";
import { Checkbox } from "@egaranti/components";
import { Pagination } from "@egaranti/components";
import { Tabs } from "@egaranti/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@egaranti/components";
import { TableSkeleton } from "@egaranti/components";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FilterSheet from "./filterSheet";

import BaseProductLinkageService from "@/services/baseProductLinkageService";

import EmptyTable from "@/components/shared/EmptyTable";
import PageSizeSelect from "@/components/shared/pageSizeSelect";
import TableHeaderWrapper from "@/components/shared/tableHeaderWrapper";

const ProductLinkage = ({ id, path, additionalProps }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState(1);
  const [openFilterSheet, setOpenFilterSheet] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);

  // Data states
  const [linkedProducts, setLinkedProducts] = useState({
    data: [],
    filter: { page: 1, size: 10 },
  });
  const [unlinkedProducts, setUnlinkedProducts] = useState({
    data: [],
    filter: { page: 1, size: 10 },
  });

  const service = new BaseProductLinkageService(path);

  // Fetch data
  const fetchProducts = async () => {
    setLoading(true);
    try {
      if (activeTab === 1) {
        const response = await service.getLinkedProducts(
          id,
          linkedProducts.filter,
        );
        setLinkedProducts({
          data: response.data,
          filter: {
            ...linkedProducts.filter,
            totalPages: response.filter.totalPages,
            totalElements: response.filter.totalElements,
          },
        });
      } else {
        const response = await service.getUnlinkedProducts(
          id,
          unlinkedProducts.filter,
        );
        setUnlinkedProducts({
          data: response.data,
          filter: {
            ...unlinkedProducts.filter,
            totalPages: response.filter.totalPages,
            totalElements: response.filter.totalElements,
          },
        });
      }
    } catch (err) {
      toast({
        description: err.response?.data?.errors[0]?.message,
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const onFilterChange = (newFilter) => {
    if (activeTab === 1) {
      setLinkedProducts((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          ...newFilter,
          page: newFilter.page || 1, // Reset page when filters change
        },
      }));
    } else {
      setUnlinkedProducts((prev) => ({
        ...prev,
        filter: {
          ...prev.filter,
          ...newFilter,
          page: newFilter.page || 1, // Reset page when filters change
        },
      }));
    }
  };

  useEffect(() => {
    fetchProducts();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchProducts();
    }
  }, [
    linkedProducts.filter.page,
    linkedProducts.filter.size,
    linkedProducts.filter.name,
    linkedProducts.filter.mainCategoryId,
    linkedProducts.filter.categoryId,
    linkedProducts.filter.subCategoryId,
    linkedProducts.filter.subCategoryBrandId,
  ]);

  useEffect(() => {
    if (activeTab === 2) {
      fetchProducts();
    }
  }, [
    unlinkedProducts.filter.page,
    unlinkedProducts.filter.size,
    unlinkedProducts.filter.name,
    unlinkedProducts.filter.mainCategoryId,
    unlinkedProducts.filter.categoryId,
    unlinkedProducts.filter.subCategoryId,
    unlinkedProducts.filter.subCategoryBrandId,
  ]);

  // Actions
  const handleAction = async () => {
    setPending(true);
    try {
      if (activeTab === 2) {
        await service.linkProducts(id, {
          merchantProductIds: selectedProducts,
        });
        toast({
          title: t("settingsPage.productsAddedSuccessfully"),
          variant: "success",
        });
      } else {
        await service.unlinkProducts(id, {
          merchantProductIds: selectedProducts,
        });
        toast({
          title: t("settingsPage.productsRemovedSuccessfully"),
          variant: "success",
        });
      }
      setSelectedProducts([]);
      fetchProducts();
    } catch (err) {
      toast({
        description: err.response?.data?.errors[0]?.message,
        variant: "error",
      });
    } finally {
      setPending(false);
    }
  };

  const handleBulkAction = async () => {
    try {
      const currentFilter =
        activeTab === 1 ? linkedProducts.filter : unlinkedProducts.filter;
      if (activeTab === 2) {
        await service.linkAllProducts(id, currentFilter);
      } else {
        await service.unlinkAllProducts(id, currentFilter);
      }
      toast({
        title:
          activeTab === 2
            ? t("settingsPage.productsAdded")
            : t("settingsPage.productsDeleted"),
        description: t("settingsPage.thisCanTakeSomeTime"),
        variant: "success",
      });
      fetchProducts();
    } catch (err) {
      toast({
        description: err.response?.data?.errors[0]?.message,
        variant: "error",
      });
    }
  };

  const currentData = activeTab === 1 ? linkedProducts : unlinkedProducts;

  if (loading) {
    return (
      <Table>
        <TableSkeleton colSpan={4} />
      </Table>
    );
  }

  return (
    <div className="formBox">
      <h2 className="mb-5 font-medium">{t("settingsPage.selectedProducts")}</h2>
      <div className="border-b">
        <Tabs
          tabs={[
            { id: 1, title: t("settingsPage.affiliatedProducts") },
            { id: 2, title: t("settingsPage.addProduct") },
          ]}
          selectedTabId={activeTab}
          onTabChange={(id) => {
            setActiveTab(id);
            setSelectedProducts([]);
          }}
        />
      </div>

      <TableHeaderWrapper className="flex items-center justify-between gap-5">
        <div className="relative ml-auto">
          <Button
            onClick={() => setOpenFilterSheet(true)}
            variant="secondaryGray"
            className="min-w-[150px] font-medium"
          >
            {t("common.addFilter")}
          </Button>

          {((activeTab === 1 && hasActiveFilters(linkedProducts.filter)) ||
            (activeTab === 2 && hasActiveFilters(unlinkedProducts.filter))) && (
            <div className="absolute -right-1 -top-1 h-3 w-3 rounded-full bg-blue-700 text-center"></div>
          )}
        </div>
      </TableHeaderWrapper>

      {currentData.data?.length ? (
        <>
          <Table className="border border-[#EAECF0] bg-white shadow-sm">
            <TableHeader className="rounded-lg border border-[#E3E7EF] bg-[#F9FAFC] font-semibold text-[#364153]">
              <TableRow>
                <TableHead>
                  <Checkbox
                    // EPS-3961 — kutu `inline-block` + `vertical-align: baseline`.
                    // Isaretsizken ICI BOS oldugu icin taban cizgisi kendi alt
                    // kenari olur ve satira fontun alt bosluğu kadar fazladan yer
                    // gerekir; isaretlenince Radix tik SVG'sini basar, taban cizgisi
                    // icerikten gelir ve o 2 px kaybolur. Olculen: satir 58 -> 56 px,
                    // tablo 629 -> 623 px. `block` kutuyu satir ici akistan cikarir,
                    // yukseklik iki durumda da ayni kalir.
                    // EPS-4841/EPS-3961 — kutu 20x20, mobil dokunma hedefi 44 px
                    // olmali. Gorunumu bozmamak icin kutunun kendisi degil,
                    // gorunmez bir ::after katmani buyutuluyor (-inset-3 => 44 px).
                    className="relative block after:absolute after:-inset-[13px] after:content-['']"
                    checked={
                      currentData.data?.length === selectedProducts.length
                    }
                    onClick={() => {
                      if (
                        currentData.data?.length === selectedProducts.length
                      ) {
                        setSelectedProducts([]);
                      } else {
                        setSelectedProducts(
                          currentData.data?.map((product) => product.id),
                        );
                      }
                    }}
                  />
                </TableHead>
                <TableHead>{t("settingsPage.productName")}</TableHead>
                <TableHead>{t("settingsPage.brand")}</TableHead>
                <TableHead>{t("settingsPage.source")}</TableHead>
                {additionalProps?.subdomain && (
                  <TableHead>{t("settingsPage.qrLink", "QR Link")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {currentData.data?.map((product) => (
                <TableRow key={product.id}>
                  <TableCell>
                    <Checkbox
                      // EPS-3961 — bkz. yukaridaki aciklama: isaretlenince satir
                      // 2 px kisaliyordu.
                      // EPS-4841/EPS-3961 — bkz. yukarisi: 44 px dokunma hedefi.
                      className="relative block after:absolute after:-inset-[13px] after:content-['']"
                      checked={selectedProducts.includes(product.id)}
                      onClick={() => {
                        if (selectedProducts.includes(product.id)) {
                          setSelectedProducts(
                            selectedProducts.filter((id) => id !== product.id),
                          );
                        } else {
                          setSelectedProducts([
                            ...selectedProducts,
                            product.id,
                          ]);
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell>{product.name}</TableCell>
                  <TableCell>{product.brand}</TableCell>
                  <TableCell>{product.source}</TableCell>
                  {additionalProps?.subdomain && (
                    <TableCell>
                      <a
                        href={
                          "https://" +
                          additionalProps?.subdomain +
                          ".egaranti.com.tr?productId=" +
                          product.id
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:underline"
                      >
                        QR Link
                      </a>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex flex-col items-center">
            {currentData.filter.totalPages > 1 && (
              <Pagination
                totalPages={currentData.filter.totalPages}
                currentPage={currentData.filter.page}
                pageSize={currentData.filter.size}
                onPageChange={(page) =>
                  onFilterChange({ ...currentData.filter, page })
                }
              />
            )}
            <PageSizeSelect
              onPageSizeChange={(size) =>
                onFilterChange({ ...currentData.filter, size, page: 1 })
              }
              value={currentData.filter.size}
            />
          </div>
          <div className="mt-5 flex justify-end gap-2">
            {currentData.data?.length > 0 && (
              <Button onClick={handleBulkAction}>
                ({currentData.filter.totalElements}){" "}
                {activeTab === 2
                  ? t("settingsPage.addAll")
                  : t("settingsPage.deleteAll")}
              </Button>
            )}
            <Button
              className={activeTab === 1 ? "bg-red-600 text-white" : ""}
              disabled={selectedProducts.length === 0 || pending}
              onClick={handleAction}
            >
              {activeTab === 2
                ? t("settingsPage.addProducts")
                : t("settingsPage.removeProducts")}
            </Button>
          </div>
        </>
      ) : (
        <EmptyTable
          title={t("common.emptyTableNoData")}
          desc={t("common.emptyTableNoDataDesc")}
        />
      )}

      <FilterSheet
        open={openFilterSheet}
        onOpenChange={setOpenFilterSheet}
        onFilterChange={onFilterChange}
        currentFilter={
          activeTab === 1 ? linkedProducts.filter : unlinkedProducts.filter
        }
      />
    </div>
  );
};

const hasActiveFilters = (filter) => {
  return (
    filter.name ||
    filter.mainCategoryId ||
    filter.categoryId ||
    filter.subCategoryId ||
    filter.subCategoryBrandId
  );
};

export default ProductLinkage;
